import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EChartsOption } from 'echarts';
import { Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import {
    createDefaultDashboardFilterState,
    createEmptyUsageAnalyticsDashboardVm,
    mapUsageAnalyticsResponseToDashboardVm
} from './user-dashboard.mapper';
import {
    DashboardFilterState,
    DashboardKpiCardVm,
    UsageAnalyticsDashboardVm,
    UsageAnalyticsResponseApi
} from './user-dashboard.models';
import { UsageAnalyticsService } from './usage-analytics.service';
import { DetailDialogComponent, DetailDialogData } from './detail-dialog/detail-dialog.component';

@Component({
    selector: 'app-user-dashboard',
    templateUrl: './user-dashboard.component.html',
    styleUrls: ['./user-dashboard.component.scss'],
    standalone: false
})
export class UserDashboardComponent implements OnInit, OnDestroy {
    public readonly chartInitOptions = { renderer: 'svg' };

    public filters: DashboardFilterState = createDefaultDashboardFilterState();
    public dashboardVm: UsageAnalyticsDashboardVm = createEmptyUsageAnalyticsDashboardVm();
    public dailyActiveUsersChartOptions: EChartsOption = {};
    public agentUsageChartOptions: EChartsOption = {};
    public isLoading = false;
    public errorMessage = '';

    private readonly destroy$ = new Subject<void>();

    constructor(
        private usageAnalyticsService: UsageAnalyticsService,
        private dialog: MatDialog
    ) { }

    public ngOnInit(): void {
        this.loadAnalytics();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onApplyFilters(): void {
        if (this.filters.startDate && this.filters.endDate && this.filters.startDate > this.filters.endDate) {
            this.errorMessage = 'Start date must be on or before end date.';
            return;
        }

        this.loadAnalytics(this.filters.startDate, this.filters.endDate);
    }

    public trackKpiCard(_index: number, card: DashboardKpiCardVm): string {
        return card.label;
    }

    public onKpiCardClick(card: DashboardKpiCardVm): void {
        if (!card.clickAction) return;

        let dialogData: DetailDialogData;
        let dialogWidth: string;

        if (card.clickAction === 'openActiveUsers') {
            dialogData = { type: 'users', users: this.dashboardVm.users };
            dialogWidth = '700px';
        } else if (card.clickAction === 'openFeedback') {
            dialogData = {
                type: 'feedback',
                feedbackItems: this.dashboardVm.feedbackItems,
                allFeedbackItems: this.dashboardVm.allFeedbackItems
            };
            dialogWidth = '740px';
        } else if (card.clickAction === 'openQuestions') {
            dialogData = { type: 'questions', questionItems: this.dashboardVm.userAgentQuestions };
            dialogWidth = '900px';
        } else {
            return;
        }

        this.dialog.open(DetailDialogComponent, {
            data: dialogData,
            width: dialogWidth,
            maxWidth: '95vw',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            panelClass: 'ud-dialog-panel',
        });
    }

    public openConsolidatedReport(): void {
        this.dialog.open(DetailDialogComponent, {
            data: {
                type: 'consolidated',
                managerStats: this.dashboardVm.managerStats
            },
            width: '860px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            panelClass: 'ud-dialog-panel',
        });
    }

    public get hasDailyActiveUsersData(): boolean {
        return this.dashboardVm.activeUserSeries.length > 0;
    }

    public get hasAgentUsageData(): boolean {
        return this.dashboardVm.agentUsageItems.some((item) => item.count > 0);
    }

    public get hasMatchedActivity(): boolean {
        return this.dashboardVm.matchedRows > 0;
    }

    public get canShowConsolidatedReportButton(): boolean {
        return !!this.lastModifiedDisplay;
    }

    public get lastModifiedDisplay(): string {
        const isoTimestamp = this.dashboardVm.lastModifiedTime;
        if (!isoTimestamp) {
            return '';
        }

        const parsedDate = new Date(isoTimestamp);
        if (Number.isNaN(parsedDate.getTime())) {
            return '';
        }

        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        }).format(parsedDate);
    }

    private loadAnalytics(startDate?: string, endDate?: string): void {
        const cachedResponse = this.usageAnalyticsService.getCachedUsageAnalytics(startDate, endDate);

        if (cachedResponse) {
            this.applyDashboardData(cachedResponse);
            this.errorMessage = '';
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        this.usageAnalyticsService.getUsageAnalytics(startDate, endDate)
            .pipe(
                tap((response) => {
                    this.usageAnalyticsService.cacheUsageAnalytics(response, startDate, endDate);
                }),
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response) => {
                    this.applyDashboardData(response);
                },
                error: (error) => {
                    this.errorMessage = error?.error?.error || 'Unable to load usage analytics right now.';
                    this.buildChartOptions();
                }
            });
    }

    private applyDashboardData(response: UsageAnalyticsResponseApi): void {
        this.dashboardVm = mapUsageAnalyticsResponseToDashboardVm(response);
        this.filters = { ...this.dashboardVm.filterState };
        this.buildChartOptions();
    }

    private buildChartOptions(): void {
        this.dailyActiveUsersChartOptions = this.createDailyActiveUsersChartOptions();
        this.agentUsageChartOptions = this.createAgentUsageChartOptions();
    }

    private createDailyActiveUsersChartOptions(): EChartsOption {
        return {
            animationDuration: 400,
            color: ['#0f62fe'],
            grid: {
                left: 24,
                right: 24,
                top: 24,
                bottom: 24,
                containLabel: true
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'line'
                },
                valueFormatter: (value) => `${value ?? 0} users`
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: this.dashboardVm.activeUserSeries.map((point) => point.label),
                axisLabel: {
                    color: '#525252'
                },
                axisLine: {
                    lineStyle: {
                        color: '#d9d9d9'
                    }
                }
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                axisLabel: {
                    color: '#525252'
                },
                splitLine: {
                    lineStyle: {
                        color: '#f0f0f0'
                    }
                }
            },
            series: [
                {
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 8,
                    areaStyle: {
                        opacity: 0.12
                    },
                    lineStyle: {
                        width: 3
                    },
                    data: this.dashboardVm.activeUserSeries.map((point) => point.value)
                }
            ]
        };
    }

    private createAgentUsageChartOptions(): EChartsOption {
        return {
            animationDuration: 400,
            color: ['#4589ff'],
            grid: {
                left: 24,
                right: 24,
                top: 24,
                bottom: 24,
                containLabel: true
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: (params: any) => {
                    const seriesPoint = Array.isArray(params) ? params[0] : params;
                    const item = this.dashboardVm.agentUsageItems[seriesPoint?.dataIndex ?? -1];

                    if (!item) {
                        return '';
                    }

                    return `${item.agent}<br/>Count: ${item.count}<br/>Share: ${item.percentage.toFixed(1)}%`;
                }
            },
            xAxis: {
                type: 'value',
                minInterval: 1,
                axisLabel: {
                    color: '#525252'
                },
                splitLine: {
                    lineStyle: {
                        color: '#f0f0f0'
                    }
                }
            },
            yAxis: {
                type: 'category',
                axisLabel: {
                    color: '#262626',
                    width: 140,
                    overflow: 'truncate'
                },
                axisLine: {
                    lineStyle: {
                        color: '#d9d9d9'
                    }
                },
                data: this.dashboardVm.agentUsageItems.map((item) => item.agent)
            },
            series: [
                {
                    type: 'bar',
                    barMaxWidth: 24,
                    data: this.dashboardVm.agentUsageItems.map((item) => item.count),
                    label: {
                        show: true,
                        position: 'right',
                        color: '#262626',
                        formatter: ({ dataIndex }: { dataIndex: number }) => {
                            const item = this.dashboardVm.agentUsageItems[dataIndex];
                            return item ? `${item.count}` : '';
                        }
                    }
                }
            ]
        };
    }
}
